import { Index, type JSX } from 'solid-js';
import { t } from '../i18n';
import { type OverflowPill, PILL_H } from '../lib/fleet-live-deck';
import type { TipState } from './FleetLiveTip';

// A crowded lane's "+N" pill, made reachable (#6778, review 4404758128): the canvas paints
// it, and this lays a real button over it, so a keyboard reaches it with Tab and a screen
// reader hears every hidden item in its name. Focus or hover opens the tooltip listing them
// (the first `OVERFLOW_TIP_LINES`, then "and N more"); Escape, blur and leaving close it.
// `<Index>` keeps each button across frames, so a patch never drops the focus.

export const OVERFLOW_TIP_LINES = 12;

export function overflowTip(o: OverflowPill, x: number, y: number): TipState {
  const rest = o.hidden.length - OVERFLOW_TIP_LINES;
  return {
    x,
    y,
    title: t('fleet.live.overflow_title', { count: o.hidden.length }),
    lines: o.hidden.slice(0, OVERFLOW_TIP_LINES).map((i) => i.label),
    more: rest > 0 ? t('fleet.live.overflow_tip_more', { count: rest }) : null,
  };
}

export function FleetLiveOverflow(props: {
  readonly overflow: readonly OverflowPill[];
  readonly onTip: (tip: TipState | null) => void;
  readonly onHover: (key: string | null) => void;
}): JSX.Element {
  const close = (): void => {
    props.onTip(null);
    props.onHover(null);
  };
  return (
    <Index each={props.overflow}>
      {(o) => {
        const open = (x: number, y: number): void => {
          props.onTip(overflowTip(o(), x, y));
          props.onHover(o().key);
        };
        return (
          <button
            type="button"
            class="fleet-live__overflow"
            style={{
              left: `${o().x}px`,
              top: `${o().y - PILL_H / 2}px`,
              width: `${o().w}px`,
              height: `${PILL_H}px`,
            }}
            aria-label={t('fleet.live.overflow_label', {
              count: o().hidden.length,
              items: o()
                .hidden.map((i) => i.label)
                .join(', '),
            })}
            onFocus={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              open(r.left, r.bottom);
            }}
            onBlur={close}
            onKeyDown={(e) => {
              if (e.key === 'Escape') close();
            }}
            onPointerEnter={(e) => open(e.clientX, e.clientY)}
            onPointerLeave={close}
          />
        );
      }}
    </Index>
  );
}
